import {
  FC,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useState,
  ChangeEvent,
  useRef,
  useContext
} from 'react';
import Joi from 'joi';
import { Checkbox, FormHelperText, FormLabel, Grid, Button, Typography, Box } from '@mui/material';
import InputText from '../form/InputText';
import { ReactComponent as BriefcaseIcon } from '../../assets/images/wrapped-briefcase.svg';
import SwipeableDrawerHeader from '../../components/swipeable-drawer-header/SwipeableDrawerHeader';
import FieldContainerExternal from '../../components/field-container/FieldContainer';
import InputRadio from '../form/InputRadio';
import AssignVesselsModal from '../../components/vessels-tags-input/AssignVesselsModal';
import UserProfile from '../user-profile/UserProfile';
import { validateForm } from '../../utils/form';
import { userInitialState } from '../../context/reducers/customerReducer';
import './UserForm.scss';
import FormTypesEnum from '../../enums/formTypes';
import {
  UserInterface,
  UserBasicInfo,
  UsersStateInterface,
  UserType
} from '../../utils/types/user';
import RoleEnum from '../../enums/roles';
import { VesselInterface } from '../../utils/types/vessel';
import { myProfileInitialState } from '../../context/reducers/myProfileReducer';
import { MyProfileContext } from '../../context/myProfileContext';
import { VesselContext } from '../../context/vesselContext';
import { defaultPagination } from '../../utils/constants/defaults';
import { Link } from 'react-router-dom';

interface UserFormArgs {
  onClickClose: () => void;
  onSubmitForm: (form: UserInterface) => void;
  user: UserInterface;
  schema: Joi.ObjectSchema<any>;
  userState: UsersStateInterface;
  type: FormTypesEnum;
}

const FieldContainer: FC<PropsWithChildren> = ({ children }): ReactElement => {
  return <FieldContainerExternal children={children} xs={10} sm={8} md={7} lg={7} xl={7} />;
};

const UserForm: FC<UserFormArgs> = ({
  onClickClose,
  onSubmitForm: submitForm,
  user,
  schema,
  userState,
  type
}): ReactElement => {
  const ref = useRef<{ getUserBasicInfo(): void }>(null);
  const [form, setForm] = useState<UserInterface>(user);
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState(userInitialState);
  const [wasLoading, setWasLoading] = useState(userState.isLoading);

  const [role, setRole] = useState(user?.role);
  const [isVesselsModalOpen, setIsVesselsModalOpen] = useState(false);

  const { viewMyProfileInfo, state: myProfileState } = useContext(MyProfileContext);
  const { listVesselsPaginated, state: vesselState } = useContext(VesselContext);

  const [myProfile, setMyProfile] = useState<UserBasicInfo>(myProfileInitialState);

  useEffect(() => {
    listVesselsPaginated({
      ...defaultPagination,
      attributes: ['id', 'active', 'hullNumber', 'name'],
      perPage: 150
    });
  }, [role]);

  useEffect(() => {
    if (
      !myProfileState.viewInfo.error &&
      myProfileState.viewInfo.done &&
      myProfileState.viewInfo.data
    ) {
      setMyProfile(myProfileState.viewInfo.data);
    }
  }, [myProfileState.viewInfo]);

  useEffect(() => {
    viewMyProfileInfo();
  }, []);

  useEffect(() => {
    setForm(user);
  }, [user]);

  useEffect(() => {
    if (userState.error.text) setFormErrorMessage(userState.error.text);
    else setFormErrorMessage('');
  }, [userState.error]);

  useEffect(() => {
    if (wasLoading && !userState.isLoading && !userState.error.text) {
      onClickClose();
    }
    setWasLoading(userState.isLoading);
  }, [userState.isLoading]);

  const handleCheckBoxStateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setForm((prevState) => ({
      ...prevState,
      status: event.target?.checked ? 'ENABLED' : 'DISABLED'
    }));
  };

  const handleRoleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as UserType;
    setRole(value);
    setForm((prevState) => ({
      ...prevState,
      role: value
    }));
  };

  const onChangeAssignedVesselsTags = (value: any) => {
    setForm((prevState) => ({
      ...prevState,
      assignedVessels: value
    }));
  };

  const handleSubmit = (userBasicInfo: UserBasicInfo) => {
    const newForm: UserInterface = {
      ...form,
      name: userBasicInfo.name,
      lastName: userBasicInfo.lastName,
      email: userBasicInfo.email,
      phone: userBasicInfo.phone,
      status: form.status || 'DISABLED',
      role: form.role
    };

    const { errorSummary } = validateForm(schema, newForm);

    setValidationErrors(errorSummary);
    if (!Object.values(errorSummary).length) {
      submitForm(newForm);
    }
  };

  const handleClose = () => {
    onClickClose();
    setForm(user);
    setValidationErrors(userInitialState);
  };

  const onClickSave = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    if (ref.current) ref.current.getUserBasicInfo();
  };

  const allVessels = (vesselState?.list?.data?.results ||
    (Array.isArray(vesselState?.list?.data) ? vesselState.list.data : [])) as VesselInterface[];
  const userVessels = allVessels.filter((vessel) => {
    const ids = Array.isArray(vessel.user_id) ? vessel.user_id : [vessel.user_id];
    return ids.includes(user.id);
  });
  const assignedVessels = form.assignedVessels || [];
  const isCustomer = role === RoleEnum.CUSTOMER;

  const formTitle = isCustomer
    ? type === FormTypesEnum.CREATE
      ? 'Add new customer'
      : 'Edit customer'
    : type === FormTypesEnum.CREATE
      ? 'Add new Operations member'
      : 'Edit Operations Member';

  const fields = (
    <>
      <UserProfile
        ref={ref}
        user={user}
        validationErrors={validationErrors}
        sendForm={handleSubmit}
      />

      {!isCustomer && (
        <FieldContainer>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body1" sx={{ color: '#273f82', fontWeight: 700 }}>
              Assign vessels
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Typography variant="body2" sx={{ color: '#232735' }}>
                {assignedVessels.length} of {allVessels.length} vessels assigned
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setIsVesselsModalOpen(true)}
                sx={{
                  textTransform: 'none',
                  borderColor: '#273f82',
                  color: '#273f82',
                  borderRadius: '8px',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    borderColor: '#1f3268',
                    backgroundColor: 'rgba(39, 63, 130, 0.04)'
                  }
                }}>
                Select Vessels
              </Button>
            </Box>
          </Grid>
        </FieldContainer>
      )}

      {!isCustomer && (
        <AssignVesselsModal
          key={isVesselsModalOpen ? 'open' : 'closed'}
          open={isVesselsModalOpen}
          onClose={() => setIsVesselsModalOpen(false)}
          allVessels={allVessels}
          selectedVessels={assignedVessels}
          onConfirm={onChangeAssignedVesselsTags}
        />
      )}

      <div style={{ height: 24, width: '100%' }} />
      {myProfile.role === RoleEnum.ADMIN && role !== RoleEnum.CUSTOMER && (
        <FieldContainer>
          <InputRadio
            name="role"
            label="Role"
            value={role}
            onChange={handleRoleChange}
            options={[
              { label: 'Admin', value: RoleEnum.ADMIN },
              { label: 'Operator', value: RoleEnum.OPERATOR },
              { label: 'Captain', value: RoleEnum.CAPTAIN }
            ]}
          />
        </FieldContainer>
      )}

      <Grid item xs={10} sm={8} md={7} lg={7} xl={7} style={checkBoxes}>
        <div>
          <Checkbox
            style={checkBoxStyle}
            checked={form.status === 'ENABLED'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleCheckBoxStateChange(e)}
          />
          <FormLabel component="label" className={'regular'}>
            Active
          </FormLabel>
        </div>
      </Grid>

      {userVessels.length > 0 && (
        <Grid item xs={10} sm={8} md={7} lg={7} xl={7}>
          <Typography variant="h6">Assigned Vessels</Typography>
          <Grid container gap={2} marginTop={1}>
            {userVessels.map((vessel) => (
              <Grid item key={vessel.id}>
                <Link
                  to={`/management/vessel/sections?id=${vessel.id}`}
                  style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: '#273f82', '&:hover': { textDecoration: 'underline' } }}>
                    {vessel.name || vessel.hullNumber}
                  </Typography>
                </Link>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
    </>
  );

  const errors = (
    <FieldContainer>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <FormHelperText error>{formErrorMessage}</FormHelperText>
      </Grid>
    </FieldContainer>
  );

  return (
    <div
      className="container-user-form"
      style={{ paddingBottom: type === FormTypesEnum.EDIT ? 0 : '84px' }}>
      <Grid
        container
        spacing={0}
        direction="row"
        alignItems="center"
        margin={'auto'}
        justifyContent={'center'}>
        <SwipeableDrawerHeader
          onClickClose={handleClose}
          onClickSave={onClickSave}
          disableButtons={userState.isLoading}
          title={formTitle}
          icon={<BriefcaseIcon className={'user-icon'} />}
        />
        {fields}
        {errors}
      </Grid>
    </div>
  );
};

export default UserForm;

const checkBoxStyle = { paddingLeft: '0px' };
const checkBoxes = { display: 'flex', itemAlign: 'center', gap: '10px', paddingTop: '10px' };
